import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Box, Button, TextField, SplitButtonDropdown, Typography} from 'views/components';
import {ArrowDownward, ArrowUpward} from 'views/icons';
import {Avatar, MarkdownEditorWithAutosave, Translate, translate} from 'views/module';

import MessageTypeFilter from './MessageTypeFilter';

const MessageComposer = (props) => {
  const [newMessage, setNewMessage] = React.useState('');
  const [textFieldClicked, setTextFieldClicked] = React.useState(false);
  const [createNewMessageLoading, setCreateNewMessageLoading] = React.useState(false);
  const [openSaveAs, setOpenSaveAs] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const {
    user,
    jobRn,
    jobBackend,
    messageTypeFilter,
    setMessageTypeFilter,
    sortDir,
    setSortDir,
  } = props;

  const saveString = `JOB-MESSAGE-${jobRn}`;
  const sortDesc = 'DESC';
  const sortAsc = 'ASC';
  const saveAsOptions = [
    {
      key: 'COMMENTS',
      text: translate('career.jobDetail.Messages.NewMessage.Comments'),
    },
    {
      key: 'ANNOUNCEMENTS',
      text: translate('career.jobDetail.Messages.NewMessage.Announcements'),
    },
  ];
  const saveAsAnchorRef = React.useRef(null);

  const onSortClick = () => {
    const invertSortDir = sortDir === sortDesc ? sortAsc : sortDesc;
    setSortDir(invertSortDir);
  };

  const onSave = (event) => {
    setOpenSaveAs(false);
    if (!saveAsOptions[selectedIndex]) {
      return;
    }
    if (!newMessage || newMessage.trim() === '') {
      return;
    }
    event.preventDefault();

    const messageType = saveAsOptions[selectedIndex].key;

    setCreateNewMessageLoading(true);
    jobBackend.messages.create(messageType, newMessage, () => {
      setNewMessage('');
      localStorage.removeItem(saveString);
      setCreateNewMessageLoading(false);
      setTextFieldClicked(false);
    }, () => setCreateNewMessageLoading(false));
  };

  const onCancel = () => {
    setOpenSaveAs(false);
    setTextFieldClicked(false);
  };

  const onSaveAsToggle = () => {
    setOpenSaveAs((prevOpen) => !prevOpen);
  };

  const onSaveAsClickAway = (event) => {
    if (saveAsAnchorRef.current && saveAsAnchorRef.current.contains(event.target)) {
      return;
    }

    setOpenSaveAs(false);
  };

  const onSaveAsMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpenSaveAs(false);
  };

  return (
    <Stack
      sx={{
        py: 1,
        px: {md: 2, xs: 1},
      }}
      spacing={2}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
      >
        <MessageTypeFilter
          messageTypeFilter={messageTypeFilter}
          setMessageTypeFilter={setMessageTypeFilter}
        />
        <Button
          variant="text"
          endIcon={sortDir === sortDesc ? <ArrowDownward /> : <ArrowUpward />}
          onClick={onSortClick}
          sx={{
            'textTransform': 'none',
            'fontWeight': 500,
            'color': '#484442',
            '&:hover': {
              backgroundColor: 'rgba(118, 118, 118, 0.1)',
            },
            'borderRadius': '4px',
            'py': 0.5,
            'pl': 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{fontWeight: 500, fontSize: '12px'}}
          >
            {
              sortDir === sortDesc ?
              <Translate expression='career.jobDetail.Messages.Sort.NewestFirst'/> :
              <Translate expression='career.jobDetail.Messages.Sort.OldestFirst'/>
            }
          </Typography>
        </Button>
      </Stack>
      <Stack direction='row' spacing={2}>
        <Box>
          <Avatar
            resourceRn={user.userRn}
            detail={{
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
            }}
          />
        </Box>
        <Box sx={{width: '100%'}}>
          {
            !textFieldClicked ?
              <TextField
                variant='outlined'
                placeholder={translate('career.jobDetail.Messages.NewMessage.AddACommentOrAnnouncement')}
                onClick={() => setTextFieldClicked(true)}
                onChange={() => undefined}
                fullWidth
                sx={{
                  'height': '42px',
                  '& .MuiInputBase-root': {
                    height: '100%',
                  },
                }}
              /> :
              <Stack spacing={2}>
                <Box>
                  <MarkdownEditorWithAutosave
                    value={newMessage}
                    onChange={setNewMessage}
                    saveString={saveString}
                    autoFocus
                  />
                </Box>
                <Box xs={12}>
                  <Stack direction={'row'} spacing={1} justifyContent='flex-end'>
                    <Button variant='outlined' sx={{height: '42px'}} onClick={onCancel}>
                      <Translate expression='career.jobDetail.Messages.NewMessage.Cancel'/>
                    </Button>
                    <SplitButtonDropdown
                      options={saveAsOptions}
                      open={openSaveAs}
                      selectedIndex={selectedIndex}
                      label={saveAsOptions[selectedIndex].text}
                      onClickPrimaryButton={onSave}
                      onClickArrowButton={onSaveAsToggle}
                      onClickAway={onSaveAsClickAway}
                      onMenuItemClick={onSaveAsMenuItemClick}
                      anchorRef={saveAsAnchorRef}
                      loading={createNewMessageLoading}
                    />
                  </Stack>
                </Box>
              </Stack>
          }
        </Box>
      </Stack>
    </Stack>
  );
};

MessageComposer.propTypes = {
  jobRn: PropTypes.string.isRequired,
  jobBackend: PropTypes.object.isRequired,
  messageTypeFilter: PropTypes.shape({
    ANNOUNCEMENTS: PropTypes.bool.isRequired,
    COMMENTS: PropTypes.bool.isRequired,
  }).isRequired,
  setMessageTypeFilter: PropTypes.func.isRequired,
  sortDir: PropTypes.string.isRequired,
  setSortDir: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(MessageComposer));
